import type { ReactNode } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { Text, TextType } from 'ui-components/components/text';
import type { RequiredTextValue } from 'ui-components/components/text/text-types';

import { messages } from './messages';

import style from './section-placeholder.module.less';

type SectionPlaceholderProps = {
  children: ReactNode;
  isCustomizeMode: boolean;
  sectionTitle: RequiredTextValue;
};

/**
 * Wraps a section and shows a placeholder if the section renders empty content
 * while in customize mode. This allows users to see and arrange sections that
 * would normally be hidden for the current order (e.g., Customs on domestic orders).
 */
export const SectionPlaceholder = ({ children, isCustomizeMode, sectionTitle }: SectionPlaceholderProps) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isEmpty, setIsEmpty] = useState(false);

  useLayoutEffect(() => {
    if (isCustomizeMode && contentRef.current) {
      const hasContent = contentRef.current.childNodes.length > 0;
      setIsEmpty(!hasContent);
    } else {
      setIsEmpty(false);
    }
  }, [isCustomizeMode, children]);

  if (!isCustomizeMode) {
    return <>{children}</>;
  }

  return (
    <>
      <div ref={contentRef}>{children}</div>
      {isEmpty && (
        <div className={style['section-placeholder']}>
          <Text isBold textType={TextType.Label} value={sectionTitle} />
          <Text textType={TextType.Description} value={messages.sectionHiddenPlaceholder} />
        </div>
      )}
    </>
  );
};
